import React, {
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { CardTabListType } from 'antd/es/card';
import {
	Col,
	Empty,
	Row,
	Typography,
	Collapse,
	Alert,
	Space,
	Tooltip,
	Checkbox,
	Flex,
} from 'antd';
import Title from 'antd/lib/typography/Title';
import {
	faPencil,
	faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { CheckOutlined } from '@ant-design/icons';
import uuid from 'short-uuid';
import {
	ConsumptionRelationTypes,
	ConsumptionSetAttachedTo,
	FuelTypes,
	FuelZoneTypes,
	VesselTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { asyncForEach } from '@shared/utils/array';
import {
	DUPLICATE_SPEED_GRADE_ERROR,
	isConflictingConsumptionSet,
} from '@shared/utils/consumptionSetIdentity';
import type { ConsumptionSetProps } from '@api/models/consumption-set';
import type { PerformanceEntryProps } from '@api/models/performance-entry';
import type { OtherPerformanceEntryProps } from '@api/models/other-performance-entry';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import Card from '@client/components/Card/Card';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import EditableCellTableRedux from '@client/components/EditableTableRedux/EditableCellTableRedux';
import showErrorNotification from '@client/utils/showErrorNotification';
import APIError from '@client/utils/APIError';
import {
	createVesselConsumptionSet,
	createVesselOtherConsumptionSet,
	deleteVesselConsumptionSet,
	updateVessel,
	updateVesselConsumptionEntries,
	updateVesselConsumptionSet,
	updateVesselOtherConsumptionEntries,
} from '@client/lib/api';
import AsyncSwitch from '@client/components/Async/AsyncSwitch';
import Button from '@client/components/Button';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import Table from '@client/components/Table/Table';
import Details from '@client/components/Details';
import {
	getAtSeaEntries,
	getColumns,
	getInPortEntries,
	getOtherEntries,
	SIMPLE_COLUMNS_AT_SEA,
	SIMPLE_COLUMNS_IN_PORT,
	SIMPLE_COLUMNS_OTHER,
} from '@client/screens/fleet/VesselDetailsScreen/components/ConsumptionSets/helpers';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import styles from './ConsumptionSets.module.css';
import OtherConsumption from './OtherConsumption';

export type SimplePerformance = {
	consumptionSetId?: number;
	relation: ConsumptionRelationTypes;
	fuelZoneType: Values<typeof FuelZoneTypes>;
	fuelType: Values<typeof FuelTypes>;
	ballast?: number;
	laden?: number;
	loading?: number;
	discharging?: number;
	idle?: number;
	ballastId?: number;
	ladenId?: number;
	loadingId?: number;
	dischargingId?: number;
	idleId?: number;
	consumption?: number;
};

export type OtherSimplePerformance = {
	fuelType: Values<typeof FuelTypes>;
	ballastId?: number;
	condition?: string;
	consumption?: number;
};

type PendingSetChange = {
	id: number;
	ballastSpeed?: number;
	ladenSpeed?: number;
	name?: string;
};

type ConsAndPerformance = ConsumptionSetProps & { PerformanceEntries: Array<PerformanceEntryProps> };

type Props = {
	activeConsumptionSetId?: number | null;
	onMakePrimary?: (id: number) => void;
	inPortEntries: Array<PerformanceEntryProps> | undefined;
	refreshVessel: () => void;
	inEstimate?: boolean;
	setEditing?: React.Dispatch<SetStateAction<boolean>>;
	setBlockClose?: React.Dispatch<SetStateAction<boolean>>;
	unsavedChanges?: boolean;
	onRefresh?: () => void;
	vessel: GetVesselDetailsResponse | undefined | null;
};

const ConsumptionSets = ({
	onMakePrimary,
	inPortEntries,
	refreshVessel,
	inEstimate = false,
	setEditing,
	activeConsumptionSetId,
	unsavedChanges,
	onRefresh,
	setBlockClose,
	vessel,
}: Props) => {
	const [tabs, setTabs] = useState<Array<CardTabListType>>();
	const [currentTab, setCurrentTab] = useState<string | undefined>(
		(vessel?.defaultConsumptionSetId ?? activeConsumptionSetId)?.toString(),
	);
	const [pendingDefaultConsumptionSetId, setPendingDefaultConsumptionSetId] =
		useState<number | null | undefined>(vessel?.defaultConsumptionSetId ?? null);
	const [relevantEntry, setRelevantEntry] = useState<ConsAndPerformance>();
	const [isLoading, setLoading] = useState(false);
	const [oldVesselId] = useState<number | undefined | null>(vessel?.id);
	const [pendingSetChanges, setPendingSetChanges] = useState<Array<PendingSetChange>>([]);
	const [localSpeedData, setLocalSpeedData] = useState<{
		ballastSpeed?: number | undefined,
		ladenSpeed?: number | undefined,
	} | undefined>();

	const [localData, setLocalData] = useState<{
		atSea: {
			main: Array<SimplePerformance>;
			secondary: Array<SimplePerformance>;
		};
		inPort: {
			main: Array<SimplePerformance>;
			secondary: Array<SimplePerformance>;
		};
		other: {
			main: Array<OtherSimplePerformance>;
			secondary: Array<OtherSimplePerformance>;
		};
	}
	>({
		atSea: {
			main: [],
			secondary: [],
		},
		inPort: {
			main: [],
			secondary: [],
		},
		other: {
			main: [],
			secondary: [],
		},
	});

	const consumptionSets = useMemo(() => {
		return vessel?.consumptionSets.filter(
			(cs) => cs.attachedTo == ConsumptionSetAttachedTo.VESSEL
		) ?? [];
	}, [vessel?.consumptionSets]);

	const handleConsSetChange = (value: { [s: string]: number | string | undefined }) => {
		const id = Number(currentTab);

		if (Number.isNaN(id)) {
			return;
		}

		const change: PendingSetChange = {
			id: Number(currentTab),
			...value,
		};

		setLocalSpeedData((prev) => ({ ...prev, ...value }));

		if (pendingSetChanges.length === 0) {
			setPendingSetChanges([change]);

			return;
		}

		setPendingSetChanges((prev) => prev.map((c) => {
			if (c.id === id) {
				return {
					...c,
					...change,
				};
			}

			return c;
		}));
	};

	const handleDelete = useCallback(async (id: number) => {
		if (vessel?.id == null) {
			return;
		}

		try {
			await deleteVesselConsumptionSet(id, vessel?.id);
			refreshVessel();
			setCurrentTab(tabs?.[0]?.key);
			showSuccessNotification('Consumption set deleted');
			if (typeof onRefresh === 'function') {
				onRefresh();
			}
		} catch (e) {
			showErrorNotification('Could not delete consumption set', e as Error);
		}
	}, [refreshVessel, onRefresh, tabs, vessel?.id]);

	useEffect(() => {
		if (vessel?.id !== oldVesselId) {
			setTabs(undefined);
			setCurrentTab(undefined);
		}
	}, [oldVesselId, vessel?.id]);

	useEffect(() => {
		if (currentTab == null) {
			const tab = tabs?.[0]?.key;

			if (tab == null) {
				return;
			}

			setCurrentTab(tab);

			const entry = consumptionSets.find((s) => s.id.toString() === tab);

			if (entry == null) {
				return;
			}

			setRelevantEntry(entry);
		}
	}, [consumptionSets, currentTab, tabs]);

	useEffect(() => {
		setPendingDefaultConsumptionSetId(vessel?.defaultConsumptionSetId ?? null);
	}, [vessel?.defaultConsumptionSetId]);

	useEffect(() => {
		if (isLoading) {
			return;
		}

		const entry = consumptionSets.find((s) => s.id.toString() === currentTab);
		setRelevantEntry(entry);

		const atSea = getAtSeaEntries(entry);
		const inPort = getInPortEntries(inPortEntries ?? []);
		const other = getOtherEntries(vessel?.otherPerformanceEntries ?? []);

		// This is the inital data
		if (localData.atSea.main.length === 0) {
			setLocalData({
				atSea,
				inPort,
				other,
			});
		}

	}, [
		consumptionSets, currentTab, inPortEntries,
		isLoading, localData.atSea.main.length, localData.inPort.main.length,
		localData.other.main.length, vessel?.otherPerformanceEntries
	]);

	useEffect(() => {
		setLocalSpeedData((prev) => ({
			ballastSpeed: relevantEntry?.ballastSpeed ?? prev?.ballastSpeed,
			ladenSpeed: relevantEntry?.ladenSpeed ?? prev?.ladenSpeed
		}));
	}, [relevantEntry]);

	useEffect(() => {
		if (isLoading) {
			return;
		}

		const other = getOtherEntries(vessel?.otherPerformanceEntries ?? []);

		if (other.main.length !== localData.other.main.length && !localData.other.main.some(
			(x) => typeof x.ballastId === 'string',
		)) {
			setLocalData((prevLocalData) => ({
				...prevLocalData,
				other,
			}));
		}
	}, [
		isLoading, localData.other.main,
		localData.other.main.length, vessel?.otherPerformanceEntries
	]);

	const handleTabChange = (tab: string) => {
		setCurrentTab(tab);

		const entry = consumptionSets.find((s) => s.id.toString() === tab);

		if (entry == null) {
			return;
		}

		setRelevantEntry(entry);

		const atSea = getAtSeaEntries(entry);

		setLocalData((prev) => ({
			...prev,
			atSea,
		}));

		setLocalSpeedData({
			ballastSpeed: entry.ballastSpeed,
			ladenSpeed: entry.ladenSpeed,
		});
	};

	const updateConsumptionSet = async (values: { [s: string]: any }) => {
		if (vessel?.id == null) {
			return;
		}

		if (changesReady) {
			await updateEntriesOnServer(false);
		}

		try {
			await updateVesselConsumptionSet({
				consumptionSetId: Number(currentTab),
				vesselId: vessel.id,
				attributes: values,
			});
			refreshVessel();
			if (typeof onRefresh === 'function') {
				onRefresh();
			}
		} catch (e) {
			showErrorNotification('Could not update consumption set', e as Error);
		}
	};

	const getDiff = useCallback(() => {
		const { inPort } = localData;
		const { atSea } = localData;
		const { other } = localData;

		const transformedInPort = [
			...inPort.main,
			...inPort.secondary,
		].map((v) => {
			const loading = {
				id: v.loadingId,
				consumption: v.loading,
				fuelType: v.fuelType,
			};

			const discharging = {
				id: v.dischargingId,
				consumption: v.discharging,
				fuelType: v.fuelType,
			};

			const idle = {
				id: v.idleId,
				consumption: v.idle,
				fuelType: v.fuelType,
			};

			return [loading, discharging, idle];
		}).flat();

		const transformedOther = [
			...other.main,
			...other.secondary,
		].map((v) => {
			const otherConsumption = {
				id: v.ballastId,
				consumption: v.consumption,
				fuelType: v.fuelType,
				condition: v.condition,
			};

			return [otherConsumption];
		}).flat();

		const transformedAtSea = [
			...atSea.main,
			...atSea.secondary,
		].map((v) => {
			const ballast = {
				id: v.ballastId,
				consumption: v.ballast,
				fuelType: v.fuelType,
			};

			const laden = {
				id: v.ladenId,
				consumption: v.laden,
				fuelType: v.fuelType,
			};

			return [ballast, laden];
		}).flat();

		const together = [...transformedAtSea, ...transformedInPort, ...transformedOther];

		const performanceEntries =
			consumptionSets.map((p) => p.PerformanceEntries).flat() ?? [];

		const allOldEntries = [
			...(inPortEntries ?? []),
			...performanceEntries,
			...vessel?.otherPerformanceEntries as PerformanceEntryProps[] ?? []
		];

		const toUpdate: Array<{
			id?: number;
			consumption?: number;
			fuelType?: Values<typeof FuelTypes>;
			condition?: string;
		}> = [];

		together.forEach((newEntry) => {
			const oldEntry = allOldEntries.find((old) => old.id === newEntry.id);

			if (oldEntry == null && typeof newEntry.id !== 'string') {
				return;
			}

			if (
				typeof newEntry.id === 'string' ||
				oldEntry?.consumption !== newEntry.consumption ||
				oldEntry?.fuelType !== newEntry.fuelType ||
				(('condition' in oldEntry && 'condition' in newEntry) &&
					(oldEntry.condition !==
						(newEntry as OtherPerformanceEntryProps).condition))
			) {
				toUpdate.push(newEntry as OtherPerformanceEntryProps);
			}
		});

		return toUpdate;
	}, [consumptionSets, inPortEntries, localData, vessel?.otherPerformanceEntries]);

	const makePrimary = async () => {
		if (relevantEntry != null && onMakePrimary != null) {
			onMakePrimary(relevantEntry.id);
		}
	};

	const changesReady = useMemo(() => {
		const defaultChanged =
			(vessel?.defaultConsumptionSetId ?? null) !==
			(pendingDefaultConsumptionSetId ?? null);

		const changes =
			getDiff().length > 0 ||
			pendingSetChanges.length > 0 ||
			defaultChanged;

		setBlockClose?.(changes);

		return changes;
	}, [
		getDiff,
		pendingSetChanges.length,
		vessel?.defaultConsumptionSetId,
		pendingDefaultConsumptionSetId
	]);

	const getTabs = useCallback((
		sets: Array<ConsumptionSetProps> | undefined,
		isPreview: boolean = false,
	): Array<CardTabListType> => {
		const isActive = (id: number) => id === activeConsumptionSetId;

		if (sets === undefined) {
			return [];
		}

		return sets
			.map((s) => {
				const label = changesReady ? (
					<Tooltip title="You have unsaved changes. Please save these before changing consumption profile.">
						{s.name}
					</Tooltip>
				) : s.name;

				return ({
					label: (isActive != null && isActive(s.id)) ? `${s.name} (active)` : label,
					key: s.id.toString(),
					id: s.id.toString(),
					disabled: changesReady && !isPreview,
					closeable: sets.length > 1,
					closeIcon: (
						sets.length > 1 && (
							<Button
								danger
								type="text"
								icon={(<FontAwesomeIcon icon={faTrash} />)}
								onClick={async () => {
									await handleDelete(s.id);
								}}
								confirmTitle="Are you sure you want to delete this entry?"
							/>
						)
					),
				});
			})
			.sort((a, b) => Number(a.id) - Number(b.id));
	}, [handleDelete, activeConsumptionSetId, changesReady]);

	useEffect(() => {
		if (consumptionSets != null && tabs == null) {
			setTabs(getTabs(consumptionSets));
		}
	}, [consumptionSets, getTabs, tabs, activeConsumptionSetId]);

	const updateEntriesOnServer = useCallback(async (_refresh?: boolean) => {
		if (vessel?.id == null) {
			return;
		}

		setLoading(true);

		let diff = getDiff();

		const defaultChanged = (vessel?.defaultConsumptionSetId ?? null) !== (pendingDefaultConsumptionSetId ?? null);

		if (diff.length === 0 && pendingSetChanges.length === 0 && !defaultChanged) {
			setLoading(false);

			return;
		}

		const fuelTypeByEntryId = new Map<number, Values<typeof FuelTypes>>();
		localData.atSea.main.forEach((row) => {
			if (row.ballastId != null) {
				fuelTypeByEntryId.set(row.ballastId, row.fuelType);
			}

			if (row.ladenId != null) {
				fuelTypeByEntryId.set(row.ladenId, row.fuelType);
			}
		});
		diff.forEach((entry) => {
			if (entry.id != null && typeof entry.id === 'number' && entry.fuelType != null) {
				fuelTypeByEntryId.set(entry.id, entry.fuelType);
			}
		});

		const projectedSets = consumptionSets.map((set) => {
			const pending = pendingSetChanges.find((change) => change.id === set.id);

			return {
				id: set.id,
				ladenSpeed: pending?.ladenSpeed ?? set.ladenSpeed,
				ballastSpeed: pending?.ballastSpeed ?? set.ballastSpeed,
				PerformanceEntries: set.PerformanceEntries.map((entry) => ({
					...entry,
					fuelType: fuelTypeByEntryId.get(entry.id) ?? entry.fuelType,
				})),
			};
		});

		for (let i = 0; i < projectedSets.length; i++) {
			for (let j = i + 1; j < projectedSets.length; j++) {
				if (isConflictingConsumptionSet(projectedSets[i], projectedSets[j])) {
					showErrorNotification(
						'Error',
						new APIError(DUPLICATE_SPEED_GRADE_ERROR, true, 400),
					);
					setLoading(false);

					return;
				}
			}
		}

		try {
			// Persist fuel grades before speeds. New profiles default to VLSFO/LSMGO at 0 kn;
			// saving 10 kn first while grades are still unchanged would falsely conflict with
			// an existing 10 kn VLSFO/LSMGO profile even when the user also switched grades.
			if (diff.some((d) => typeof d.id === 'string')) {
				const stringIdEntries = diff.filter((d) => typeof d.id === 'string');
				localData.other.main = [];

				for (const d of stringIdEntries) {
					delete d.id;
					await createVesselOtherConsumptionSet(vessel?.id, d.condition, d.consumption, d.fuelType);
				}

				diff = diff.filter((d) => typeof d.id !== 'string');
			}

			if (diff.length > 0) {
				if (diff.some((d) => typeof d.condition === 'string' && 'id' in d)) {
					await updateVesselOtherConsumptionEntries({ vesselId: vessel?.id, attributes: diff });
				} else {
					await updateVesselConsumptionEntries({
						vesselId: vessel?.id,
						attributes: diff,
					});
				}
			}

			if (pendingSetChanges.length > 0) {
				await asyncForEach(pendingSetChanges, async (setChange) => {
					const attributes = {
						name: setChange.name,
						ladenSpeed: setChange.ladenSpeed,
						ballastSpeed: setChange.ballastSpeed,
					};
					await updateVesselConsumptionSet({
						consumptionSetId: setChange.id,
						vesselId: vessel?.id,
						attributes,
					});
				});
			}

			if (defaultChanged && vessel?.id != null) {
				await updateVessel(vessel.id, {
					defaultConsumptionSetId: pendingDefaultConsumptionSetId ?? null,
				});
			}

			if (typeof onRefresh === 'function') {
				onRefresh();
			}

			refreshVessel();
			setPendingSetChanges([]);

			setLoading(false);
		} catch (e) {
			showErrorNotification('Could not update consumption entry', e as Error);
			setLoading(false);
		}
	}, [
		vessel?.id,
		getDiff,
		pendingSetChanges,
		onRefresh,
		refreshVessel,
		localData.other,
		localData.atSea.main,
		consumptionSets,
		vessel?.defaultConsumptionSetId,
		pendingDefaultConsumptionSetId,
	]);

	const addEntry = () => {
		const newPerformance: OtherSimplePerformance = {
			ballastId: uuid.generate() as any,
			fuelType: FuelTypes.VLSFO,
			condition: '',
			consumption: 0,
		};

		setLocalData((prevData) => ({
			...prevData,
			other: {
				...prevData.other,
				main: prevData.other.main.concat(newPerformance),
			},
		}));
	};

	const updateEntries = async (
		vals: Array<SimplePerformance | OtherSimplePerformance>,
		type: 'inPort' | 'atSea' | 'other',
		relation: 'main' | 'secondary'
	) => {
		if (vessel?.id == null) {
			return;
		}

		setLocalData((prev) => ({
			...prev,
			[type]: {
				...prev[type],
				[relation]: vals,
			},
		}));
	};

	const saveButton = useSaveButton({
		onSave: () => updateEntriesOnServer(true),
		loading: isLoading,
		hasPendingChanges: changesReady,
		testId: 'save-consumption',
		title: 'Save changes'
	});

	const handleAdd = async (
		_tab: string | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
		event: 'add' | 'remove',
	) => {
		if (vessel?.id == null || event === 'remove') {
			return;
		}

		try {
			await createVesselConsumptionSet(vessel?.id);
			refreshVessel();
			if (typeof onRefresh === 'function') {
				onRefresh();
			}
		} catch (e) {
			showErrorNotification('Could not create new consumption set', e as Error);
		}
	};

	const copyNormalRow = async (ecaRow: SimplePerformance, inPort: boolean = false) => {
		if (vessel?.id == null) {
			return;
		}

		const type = inPort ? 'inPort' : 'atSea';

		const [normalRow] = localData[type][ecaRow.relation]
			.filter((n) => n.fuelZoneType === FuelZoneTypes.NORMAL);

		let copiedRow = {};

		if (inPort) {
			copiedRow = {
				...ecaRow,
				loading: normalRow.loading,
				discharging: normalRow.discharging,
				idle: normalRow.idle,
			};
		} else {
			copiedRow = {
				...ecaRow,
				ballast: normalRow.ballast,
				laden: normalRow.laden,
			};
		}

		setLocalData((prev) => ({
			...prev,
			[type]: {
				...prev[type],
				[ecaRow.relation]: [normalRow, copiedRow],
			},
		}));
	};

	if (relevantEntry == null) {
		return (<Empty>No consumption data available</Empty>);
	}

	if (inEstimate) {
		const firstTable = (
			<Table
				className={styles.simpleTable}
				pagination={false}
				size="small"
				dataSource={localData.atSea.main}
				bordered
				columns={[{
					title: 'At Sea - Main',
					children: SIMPLE_COLUMNS_AT_SEA,
				}]}
			/>
		);

		const secondTable = (
			<Table
				className={styles.simpleTable}
				pagination={false}
				size="small"
				dataSource={localData.inPort.main}
				bordered
				columns={[{
					title: 'In Port - Main',
					children: SIMPLE_COLUMNS_IN_PORT,
				}]}
			/>
		);

		const thirdTable = relevantEntry.enableSecondary ? (
			<Table
				pagination={false}
				size="small"
				dataSource={localData.atSea.secondary}
				bordered
				columns={[{
					title: 'At Sea - Secondary',
					children: SIMPLE_COLUMNS_AT_SEA,
				}]}
			/>
		) : null;

		const fourthTable = vessel?.enableSecondaryInPortConsumption ? (
			<Table
				pagination={false}
				size="small"
				dataSource={localData.inPort.secondary}
				bordered
				columns={[{
					title: 'In Port - Secondary',
					children: SIMPLE_COLUMNS_IN_PORT,
				}]}
			/>
		) : null;

		const fifthTable = (
			<Table
				className={styles.simpleTable}
				pagination={false}
				size="small"
				dataSource={localData.other.main}
				bordered
				columns={[{
					title: 'Other',
					children: SIMPLE_COLUMNS_OTHER,
				}]}
			/>
		);
		const tables = [firstTable, secondTable, thirdTable, fourthTable, fifthTable];
		const filtered = tables.filter((t): t is React.ReactElement => t !== null);

		return (
			<Card
				tabList={getTabs(consumptionSets, true)}
				onTabChange={handleTabChange}
				activeTabKey={currentTab}
				tabBarExtraContent={(
					<Space>
						{(
							activeConsumptionSetId != null &&
							activeConsumptionSetId !== relevantEntry.id
						) && (<Button content="Use this consumption profile" icon={(<CheckOutlined />)} onClick={makePrimary} />)}
						<Button
							disabled={unsavedChanges}
							disabledTooltip="You have unsaved changes. Please save these before editing consumptions."
							icon={(<FontAwesomeIcon icon={faPencil} />)}
							type="primary"
							onClick={() => {
								if (typeof setEditing === 'function') {
									setEditing(true);
								}
							}}
						>
							Edit
						</Button>
					</Space>
				)}
				className={styles.smallCard}
				bordered={!inEstimate}
				slim
				size="small"
			>
				<div className={styles.simpleContainer}>
					<Row gutter={16}>
						<Col span={4} sm={6}>
							<Details
								hideHeader
								labelWidth={70}
								items={[
									{
										label: 'Ballast',
										key: 'ballastSpeed',
										value: `${relevantEntry.ballastSpeed} kt`,
									}, {
										label: 'Laden',
										key: 'ladenSpeed',
										value: `${relevantEntry.ladenSpeed} kt`,
									},
								]}
							/>
						</Col>
						<Col span={18}>
							<div className={styles.flexVertical}>
								<Space className={styles.flex} align="start">
									{filtered.slice(0, 2)}
								</Space>
								<Space className={styles.flex}>
									{filtered.slice(2, 4)}
								</Space>
							</div>
							<div className={styles.flexVertical}>
								<Space className={styles.flex} align="start">
									{filtered.slice(4, 6)}
								</Space>
							</div>
						</Col>
					</Row>
				</div>
			</Card>
		);
	}

	return (
		<Space direction="vertical">
			<Alert
				className={styles.alert}
				message={`
				Consumption figures are used for estimating bunker consumption during voyages.
				Secondary fuel types will only be included in estimated consumptions if enabled.
				`}
			/>
			<Flex
				align="center"
				justify="space-between"
				style={{ width: '100%' }}
			>
				{saveButton}
				<Tooltip
					title={
						(consumptionSets.length ?? 0) <= 1 ?
							'At least one profile must be default' :
							undefined
					}
				>
					<Checkbox
						checked={pendingDefaultConsumptionSetId === relevantEntry.id}
						onChange={(e) => {
							const isCurrentlyDefault =
								pendingDefaultConsumptionSetId === relevantEntry.id;

							if (isCurrentlyDefault && !e.target.checked) {
								return;
							}

							if (e.target.checked) {
								setPendingDefaultConsumptionSetId(relevantEntry.id);
							}
						}}
					>
						Default Profile
					</Checkbox>
				</Tooltip>
			</Flex>
			<Collapse defaultActiveKey={['atSea', 'inPort', 'otherConsumptions']}>
				<Collapse.Panel
					key="atSea"
					header={(<Title level={5} className={styles.title}>At Sea</Title>)}
					className={styles.collapse}
				>
					<Card
						tabList={getTabs(consumptionSets)}
						onTabChange={handleTabChange}
						activeTabKey={currentTab}
						className={classNames({
							[styles.card]: !inEstimate,
							[styles.cardWithMargin]: inEstimate,
						})}
						tabProps={{
							type: 'editable-card',
							onEdit: handleAdd,
							addIcon: (
								<Button
									className={styles.tabBtn}
									type="link"
									icon={(<FontAwesomeIcon size="lg" icon={faPlus} />)}
									onClick={async (e) => {
										e?.stopPropagation();
										if (changesReady) {
											await updateEntriesOnServer(true);
										}

										await handleAdd('', 'add');
									}}
									popconfirmProps={{
										onCancel: (e) => e?.stopPropagation(),
									}}
									confirmTitle={changesReady ? 'You have unsaved changes. Do you want to save?' : undefined}
									clickPropagation={false}
								/>
							),
						}}
						bordered={false}
					>
						<Row gutter={16}>
							<Col span={8}>
								<Space className={styles.space} direction="vertical">
									<EditableDetails
										key={relevantEntry.id}
										title="Details"
										labelWidth={100}
										hideButtons
										editing
										onSave={updateConsumptionSet}
										onEditValuesChange={handleConsSetChange}
										items={[
											{
												label: 'Entry name',
												key: 'name',
												type: 'text',
												editable: true,
												value: relevantEntry.name,
											},
											{
												label: 'Ballast',
												key: 'ballastSpeed',
												type: 'number',
												inputProps: {
													addonAfter: 'kt',
												},
												editable: true,
												value: localSpeedData?.ballastSpeed ?? null,
												render: (c) => (
													<>
														{c.value}
														{' '}
														kt
													</>
												),
											}, {
												label: 'Laden',
												key: 'ladenSpeed',
												type: 'number',
												inputProps: {
													addonAfter: 'kt',
												},
												editable: true,
												value: localSpeedData?.ladenSpeed ?? null,
												render: (c) => (
													<>
														{c.value}
														{' '}
														kt
													</>
												),
											},
										]}
									/>
								</Space>
							</Col>
							<Col span={16}>
								<div className={styles.flexVertical}>
									<EditableCellTableRedux<SimplePerformance>
										className={styles.consumptionTable}
										pagination={false}
										title={() => (<Title level={5} className={styles.title}>Main</Title>)}
										dataSource={localData.atSea.main}
										columns={getColumns(copyNormalRow)}
										size="small"
										onChange={(values) => updateEntries(values, 'atSea', 'main')}
										loading={isLoading}
									/>
									<EditableCellTableRedux<SimplePerformance>
										className={styles.consumptionTable}
										pagination={false}
										title={() => (
											<div className={styles.spaceBetween}>
												<Title level={5} className={styles.title}>
													Secondary
												</Title>
												<Space>
													<Typography.Text>
														Enable secondary fuel type
													</Typography.Text>
													<AsyncSwitch
														key={`switch-${relevantEntry?.id}`}
														checked={relevantEntry?.enableSecondary}
														onChange={async (v) => {
															await updateConsumptionSet({ 'enableSecondary': v });
														}}
													/>
												</Space>
											</div>
										)}
										dataSource={localData.atSea.secondary}
										columns={getColumns(copyNormalRow, !relevantEntry.enableSecondary)}
										size="small"
										onChange={(values) => updateEntries(values, 'atSea', 'secondary')}
										loading={isLoading}
									/>
								</div>
							</Col>
						</Row>
					</Card>
				</Collapse.Panel>
				<Collapse.Panel
					key="inPort"
					header={(<Title level={5} className={styles.title}>In Port</Title>)}
					className={styles.collapse}
				>
					<Space>
						<EditableCellTableRedux<SimplePerformance>
							data-testid="in-port-main-consumption-table"
							title={() => (<Title level={5} className={styles.title}>Main</Title>)}
							className={styles.consumptionTable}
							pagination={false}
							dataSource={localData.inPort.main}
							columns={getColumns(copyNormalRow, false, true)}
							size="small"
							onChange={(values) => updateEntries(values, 'inPort', 'main')}
							loading={isLoading}
						/>
						<EditableCellTableRedux<SimplePerformance>
							title={() => (
								<div className={styles.spaceBetween}>
									<Title level={5} className={styles.title}>
										Secondary
									</Title>
									<Space>
										<Typography.Text>
											Enable secondary fuel type
										</Typography.Text>
										<AsyncSwitch
											checked={vessel?.enableSecondaryInPortConsumption}
											onChange={async (v) => {
												if (vessel?.id == null) {
													return;
												}

												if (changesReady) {
													await updateEntriesOnServer(true);
												}

												await updateVessel(vessel?.id, {
													enableSecondaryInPortConsumption: v,
												});

												refreshVessel();

												if (typeof onRefresh === 'function') {
													onRefresh();
												}
											}}
										/>
									</Space>
								</div>
							)}
							className={styles.consumptionTable}
							pagination={false}
							dataSource={localData.inPort.secondary}
							columns={getColumns(copyNormalRow, !vessel?.enableSecondaryInPortConsumption, true)}
							size="small"
							onChange={(values) => updateEntries(values, 'inPort', 'secondary')}
							loading={isLoading}
						/>
					</Space>
				</Collapse.Panel>
				{(vessel?.type === VesselTypes.CHEMICAL || vessel?.type === VesselTypes.CRUDE_PRODUCT) && (
					<Collapse.Panel
						key="otherConsumptions"
						header={(<Title level={5} className={styles.title}>Other Consumptions</Title>)}
						className={styles.collapse}
					>
						<OtherConsumption
							vesselId={vessel?.id}
							vesselType={vessel?.type}
							refreshVessel={refreshVessel}
							addEntry={addEntry}
							updateEntries={updateEntries}
							isLoading={isLoading}
							localData={localData}
						/>
					</Collapse.Panel>
				)}
			</Collapse>
		</Space>
	);
};

export default ConsumptionSets;

